import {
  ITransponser,
  ITransponserFunctions,
  IUnitsConverter,
  IUnitsConverterFunctions,
  ONE_MILLIMETER_IN_FEET,
  roundDec,
} from "@tedivo/tedivo-pure-helpers";
import {
  getPreferencesValue,
  setPreferencesKeyAndValue,
} from "@tedivo/tedivo-preferences";

import LenUnitsEnum from "../enums/LenUnitsEnum";
import MassUnitsEnum from "../enums/MassUnitsEnum";
import { PortStarboardEnum } from "open-vessel-definition";
import UoLcgReferenceEnum from "../enums/UoLcgReferenceEnum";
import VcgVerticalReferenceEnum from "../enums/VcgVerticalReferenceEnum";

class GlobalUnits {
  public language = "en";
  private _lpp = 0;

  units = {
    length: LenUnitsEnum.METERS,
    mass: MassUnitsEnum.TONNES,
    lcgRef: UoLcgReferenceEnum.AFT_PERPENDICULAR_DFWD,
    tcgDir: PortStarboardEnum.PORT,
    vcgRef: VcgVerticalReferenceEnum.BOTTOM_BASE,
  };

  constructor() {
    const lengthUnitsSaved = getPreferencesValue("units-length"),
      massUnitsSaved = getPreferencesValue("units-mass"),
      lcgRefSaved = getPreferencesValue("units-lcgRef"),
      tcgDirSaved = getPreferencesValue("units-tcgRef"),
      vcgRefSaved = getPreferencesValue("units-vcgRef");

    if (lengthUnitsSaved) this.units.length = Number(lengthUnitsSaved);
    if (massUnitsSaved) this.units.mass = Number(massUnitsSaved);
    if (lcgRefSaved) this.units.lcgRef = Number(lcgRefSaved);
    if (tcgDirSaved) this.units.tcgDir = Number(tcgDirSaved);
    if (vcgRefSaved) this.units.vcgRef = Number(vcgRefSaved);
  }

  get lpp() {
    return this._lpp || 0;
  }

  setLpp = (lpp: number) => {
    this._lpp = lpp;
  };

  get lengthUnits(): IUnitsConverterFunctions<LenUnitsEnum> {
    const length = this.units.length;

    return {
      units: length,
      fromDisplayToValue: lenghtUnitsFromDisplayToValue[length],
      fromValueToDisplay: lenghtUnitsFromValueToDisplay[length],
      fromValueToFormattedDisplay: (a: number): string => {
        const nn = new Intl.NumberFormat(this.language, {
          style: "decimal",
        });
        return nn.format(lenghtUnitsFromValueToDisplay[length](Number(a)));
      },
    };
  }

  get massUnits(): IUnitsConverterFunctions<MassUnitsEnum> {
    const mass = this.units.mass;

    return {
      units: mass,
      fromDisplayToValue: massUnitsFromDisplayToValue[mass],
      fromValueToDisplay: massUnitsFromValueToDisplay[mass],
      fromValueToFormattedDisplay: (a: number): string => {
        const nn = new Intl.NumberFormat(this.language, {
          style: "decimal",
        });
        return nn.format(massUnitsFromValueToDisplay[mass](Number(a)));
      },
    };
  }

  get lcgTransposer(): ITransponserFunctions<UoLcgReferenceEnum> {
    const lpp = this.lpp;
    const lcgRef = this.units.lcgRef;

    if (lpp !== undefined) {
      return {
        transposeTo: lcgRef,
        fromDisplayToValue: (a) =>
          lcgTransponserFromDisplayToValue[lcgRef](a, lpp),
        fromValueToDisplay: (a) =>
          lcgTransponserFromValueToDisplay[lcgRef](a, lpp),
      };
    } else {
      return {
        transposeTo: lcgRef,
        fromDisplayToValue: (a) => a,
        fromValueToDisplay: (a) => a,
      };
    }
  }

  get tcgTransposer(): ITransponserFunctions<PortStarboardEnum> {
    const tcgDir = this.units.tcgDir;

    return {
      transposeTo: tcgDir,
      fromDisplayToValue: (a: number) =>
        tcgTransponserFromDisplayToValue[tcgDir](a, 0),
      fromValueToDisplay: (a: number) =>
        tcgTransponserFromValueToDisplay[tcgDir](a, 0),
    };
  }

  get vcgTransposer(): ITransponserFunctions<VcgVerticalReferenceEnum> {
    const vcgRef = this.units.vcgRef;

    return {
      transposeTo: vcgRef,
      fromDisplayToValue: (a: number) =>
        vcgTransponserFromDisplayToValue[vcgRef](a, 0),
      fromValueToDisplay: (a: number) =>
        vcgTransponserFromValueToDisplay[vcgRef](a, 0),
    };
  }

  setLengthUnits = (u: LenUnitsEnum): boolean => {
    if (this.units.length === u) return false;
    this.units.length = u;
    setPreferencesKeyAndValue("units-length", u);
    return true;
  };

  setMassUnits = (u: MassUnitsEnum): boolean => {
    if (this.units.mass === u) return false;
    this.units.mass = u;
    setPreferencesKeyAndValue("units-mass", u);
    return true;
  };

  setLcgRef = (u: UoLcgReferenceEnum): boolean => {
    if (this.units.lcgRef === u) return false;
    this.units.lcgRef = u;
    setPreferencesKeyAndValue("units-lcgRef", u);
    return true;
  };

  setTcgDir = (u: PortStarboardEnum): boolean => {
    if (this.units.tcgDir === u) return false;
    this.units.tcgDir = u;
    setPreferencesKeyAndValue("units-tcgRef", u);
    return true;
  };

  setVcgRef = (u: VcgVerticalReferenceEnum): boolean => {
    if (this.units.vcgRef === u) return false;
    this.units.vcgRef = u;
    setPreferencesKeyAndValue("units-vcgRef", u);
    return true;
  };
}

const globalUnits = new GlobalUnits();
export default globalUnits;

export type IGlobalUnits = GlobalUnits;

export const lenghtUnitsFromDisplayToValue: Record<
  LenUnitsEnum,
  IUnitsConverter
> = {
  [LenUnitsEnum.MILLIMETERS]: (a) => Math.round(a),
  [LenUnitsEnum.CENTIMETERS]: (a) => Math.round(a * 10),
  [LenUnitsEnum.METERS]: (a) => Math.round(a * 1000),
  [LenUnitsEnum.FEET]: (a) => Math.round(a / ONE_MILLIMETER_IN_FEET),
};

export const lenghtUnitsFromValueToDisplay: Record<
  LenUnitsEnum,
  IUnitsConverter
> = {
  [LenUnitsEnum.MILLIMETERS]: (a) => a,
  [LenUnitsEnum.CENTIMETERS]: (a) => a / 10,
  [LenUnitsEnum.METERS]: (a) => a / 1000,
  [LenUnitsEnum.FEET]: (a) => roundDec(a * ONE_MILLIMETER_IN_FEET, 2),
};

export const massUnitsFromDisplayToValue: Record<
  MassUnitsEnum,
  IUnitsConverter
> = {
  [MassUnitsEnum.GRAMS]: (a) => a,
  [MassUnitsEnum.KILOGRAMS]: (a) => Math.round(a * 1000),
  [MassUnitsEnum.TONNES]: (a) => Math.round(a * 1000000),
  [MassUnitsEnum.SHORT_TONS]: (a) => Math.round(a * 0.907185 * 1000000),
  [MassUnitsEnum.LONG_TONS]: (a) => Math.round(a * 1.01605 * 1000000),
  [MassUnitsEnum.POUNDS]: (a) => Math.round(a * 453.592),
  [MassUnitsEnum.POUNDS_1000]: (a) => Math.round(a * 453592),
};

export const massUnitsFromValueToDisplay: Record<
  MassUnitsEnum,
  IUnitsConverter
> = {
  [MassUnitsEnum.GRAMS]: (a) => a,
  [MassUnitsEnum.KILOGRAMS]: (a) => roundDec(a / 1000, 3),
  [MassUnitsEnum.TONNES]: (a) => roundDec(a / 1000000, 3),
  [MassUnitsEnum.SHORT_TONS]: (a) => roundDec(a / 0.907185 / 1000000, 3),
  [MassUnitsEnum.LONG_TONS]: (a) => roundDec(a / 1.01605 / 1000000, 3),
  [MassUnitsEnum.POUNDS]: (a) => roundDec(a / 453.592, 3),
  [MassUnitsEnum.POUNDS_1000]: (a) => roundDec(a / 453592, 3),
};

export const lcgTransponserFromValueToDisplay: Record<
  UoLcgReferenceEnum,
  ITransponser
> = {
  [UoLcgReferenceEnum.AFT_PERPENDICULAR_DFWD]: (a: number) => a,
  [UoLcgReferenceEnum.FWD_PERPENDICULAR_DAFT]: (a, lpp) => (lpp || 0) - a,
  [UoLcgReferenceEnum.MIDSHIPS_DFWD]: (a, lpp) => a - (lpp || 0) * 0.5,
  [UoLcgReferenceEnum.MIDSHIPS_DAFT]: (a, lpp) => (lpp || 0) * 0.5 - a,
};

export const lcgTransponserFromDisplayToValue: Record<
  UoLcgReferenceEnum,
  ITransponser
> = {
  [UoLcgReferenceEnum.AFT_PERPENDICULAR_DFWD]: (a: number) => a,
  [UoLcgReferenceEnum.FWD_PERPENDICULAR_DAFT]: (a, lpp) => (lpp || 0) - a,
  [UoLcgReferenceEnum.MIDSHIPS_DFWD]: (a, lpp) => (lpp || 0) * 0.5 + a,
  [UoLcgReferenceEnum.MIDSHIPS_DAFT]: (a, lpp) => (lpp || 0) * 0.5 - a,
};

export const tcgTransponserFromValueToDisplay: Record<
  PortStarboardEnum,
  ITransponser
> = {
  [PortStarboardEnum.PORT]: (a: number) => -a,
  [PortStarboardEnum.STARBOARD]: (a: number) => a,
};

export const tcgTransponserFromDisplayToValue: Record<
  PortStarboardEnum,
  ITransponser
> = {
  [PortStarboardEnum.PORT]: (a: number) => -a,
  [PortStarboardEnum.STARBOARD]: (a: number) => a,
};

const CONT_8_6_IN_MM = 8.5 / ONE_MILLIMETER_IN_FEET;

export const vcgTransponserFromValueToDisplay: Record<
  VcgVerticalReferenceEnum,
  ITransponser
> = {
  [VcgVerticalReferenceEnum.BOTTOM_BASE]: (a: number) => a,
  [VcgVerticalReferenceEnum.VCG_45_8_6]: (a: number) =>
    Math.round(a + CONT_8_6_IN_MM * 0.45),
  [VcgVerticalReferenceEnum.VCG_50_8_6]: (a: number) =>
    Math.round(a + CONT_8_6_IN_MM * 0.5),
};

export const vcgTransponserFromDisplayToValue: Record<
  VcgVerticalReferenceEnum,
  ITransponser
> = {
  [VcgVerticalReferenceEnum.BOTTOM_BASE]: (a: number) => a,
  [VcgVerticalReferenceEnum.VCG_45_8_6]: (a: number) =>
    Math.round(a - CONT_8_6_IN_MM * 0.45),
  [VcgVerticalReferenceEnum.VCG_50_8_6]: (a: number) =>
    Math.round(a - CONT_8_6_IN_MM * 0.5),
};
